import "./SearchStructure.scss";
import clsx from "clsx";
import { ChangeEvent, useState } from "react";
import { Button, Col } from "@/components";
import { Close } from "@/icons";
import { Structure } from "@anoki/fse-common";
import { useMediaQuery } from "@/hooks/useMediaQuery.ts";

export type SearchProps = {
  btnText: string;
  helperText: string;
  mobileHelperText?: string;
  value: string;
  items: Structure[];
  setValue: (value: string) => void;
};

export const SearchStructure = ({
  btnText,
  helperText,
  mobileHelperText = helperText,
  value,
  items,
  setValue,
}: SearchProps) => {
  const [valueInput, setValueInput] = useState<string | undefined>(value);
  const [filteredItems, setFilteredItems] = useState<Structure[]>([]);
  const isDesktop = useMediaQuery("(min-width: 768px)");

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setValueInput(inputValue);

    if (inputValue.length >= 3) {
      const results = items.filter((item) =>
        [item.address.streetName, item.address.zipCode, item.address.city].some(
          (field) => field?.toLowerCase().includes(inputValue.toLowerCase()),
        ),
      );
      setFilteredItems(results);
    } else {
      setFilteredItems([]);
    }
  };

  const handleSelectItem = (item: Structure) => {
    setValueInput(item.address.plainAddress);
    setFilteredItems([]);
  };

  const highlightMatch = (text: string, query: string) => {
    if (!query) return text;

    const index = text.toLowerCase().indexOf(query.toLowerCase());
    if (index === -1) return text;

    const beforeMatch = text.slice(0, index);
    const match = text.slice(index, index + query.length);
    const afterMatch = text.slice(index + query.length);

    return (
      <>
        {beforeMatch}
        <span className={"text-primary fw-bold"}>{match}</span>
        {afterMatch}
      </>
    );
  };

  return (
    <div className={clsx("ui-search", "br-x4 bg-border-secondary w-full")}>
      <div className={"position-relative"}>
        <input
          id="search"
          type="text"
          className={clsx(
            "ui-search-input",
            "fs-6 md-fs-custom text-slate-20 br-x4 w-full pl-x24",
            valueInput && valueInput.length >= 3 && "has-clear-icon",
          )}
          placeholder={isDesktop ? helperText : mobileHelperText}
          value={valueInput}
          onChange={handleInputChange}
          maxLength={30}
        />
        {valueInput && valueInput.length >= 3 && (
          <Close
            className={"ui-clean-button"}
            onClick={() => {
              setValueInput("");
              setFilteredItems([]);
            }}
          />
        )}
      </div>
      {filteredItems.length > 0 && (
        <div className="ui-wrap-list-item-search shadow position-absolute p-x24">
          <Col
            gap={"x24"}
            fw={"regular"}
            text={"secondary"}
            fs={"6"}
            className="ui-list-item-search overflow-y-auto "
          >
            {filteredItems.map((item, index) => (
              <div key={index} onClick={() => handleSelectItem(item)}>
                {highlightMatch(item.address.plainAddress, valueInput || "")}
              </div>
            ))}
          </Col>
        </div>
      )}
      <div className={clsx("ui-div-btn")}>
        <Button
          className={clsx(
            "ui-btn-search",
            "bg-primary text-white fs-7 md-fs-6 fw-semibold br-x4",
          )}
          onClick={() => {
            setFilteredItems([]);
            setValue(valueInput ?? "");
          }}
        >
          {btnText}
        </Button>
      </div>
    </div>
  );
};
